// MLB Season Management Utilities

// MLB Season definitions
export const MLB_SEASONS = {
  2026: {
    year: 2026,
    startDate: '2026-03-26',
    endDate: '2026-09-27',
    name: '2026 MLB Season',
    status: 'upcoming'
  },
  2025: {
    year: 2025,
    startDate: '2025-03-27',
    endDate: '2025-09-28',
    name: '2025 MLB Season',
    status: 'upcoming'
  }
};

// Get all available seasons
export const getAvailableSeasons = () => {
  return Object.values(MLB_SEASONS).sort((a, b) => b.year - a.year);
};

// Get season by year
export const getSeasonByYear = (year) => {
  return MLB_SEASONS[year];
};

// Get current or next active season
export const getCurrentSeason = () => {
  const today = new Date().toISOString().split('T')[0];
  
  // Find a season that includes today or is upcoming
  const seasons = getAvailableSeasons();
  for (const season of seasons) {
    if (today >= season.startDate && today <= season.endDate) {
      return season;
    }
  }
  
  // If no active season, return the next upcoming one
  for (const season of seasons) {
    if (today < season.startDate) {
      return season;
    }
  }
  
  // Default to 2026 season
  return MLB_SEASONS[2026];
};

// Check if a date is within a season
export const isDateInSeason = (date, season) => {
  return date >= season.startDate && date <= season.endDate;
};

// Get all dates in a season
export const getAllDatesInSeason = (season) => {
  const dates = [];
  const start = new Date(season.startDate);
  const end = new Date(season.endDate);
  
  for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
    dates.push(date.toISOString().split('T')[0]);
  }
  
  return dates;
};

// Get total days in season
export const getSeasonDayCount = (season) => {
  const start = new Date(season.startDate);
  const end = new Date(season.endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end
  return diffDays;
};

// Get current day of season (1-indexed)
export const getCurrentSeasonDay = (season, currentDate = null) => {
  const today = currentDate || new Date().toISOString().split('T')[0];
  
  if (!isDateInSeason(today, season)) {
    return null;
  }
  
  const start = new Date(season.startDate);
  const current = new Date(today);
  const diffTime = Math.abs(current - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  
  return diffDays;
};

// Format season date range
export const formatSeasonDateRange = (season) => {
  const startDate = new Date(season.startDate);
  const endDate = new Date(season.endDate);
  
  const startFormatted = startDate.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
  
  const endFormatted = endDate.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
  
  return `${startFormatted} - ${endFormatted}`;
};

// Get season progress percentage
export const getSeasonProgress = (season, currentDate = null) => {
  const today = currentDate || new Date().toISOString().split('T')[0];
  
  if (today < season.startDate) {
    return 0;
  }
  
  if (today > season.endDate) {
    return 100;
  }
  
  const totalDays = getSeasonDayCount(season);
  const currentDay = getCurrentSeasonDay(season, today);
  
  return Math.round((currentDay / totalDays) * 100);
};

// Get season status
export const getSeasonStatus = (season, currentDate = null) => {
  const today = currentDate || new Date().toISOString().split('T')[0];
  
  if (today < season.startDate) {
    return 'Pre-Season';
  }
  
  if (today > season.endDate) {
    return 'Post-Season';
  }
  
  return 'In Season';
};

// Get next valid date in season
export const getNextValidDate = (season, currentDate) => {
  if (currentDate < season.startDate) {
    return season.startDate;
  }
  
  if (currentDate > season.endDate) {
    return season.endDate;
  }
  
  return currentDate;
};

// Get previous valid date in season
export const getPreviousValidDate = (season, currentDate) => {
  const date = new Date(currentDate);
  date.setDate(date.getDate() - 1);
  const prevDate = date.toISOString().split('T')[0];
  
  if (prevDate < season.startDate) {
    return season.startDate;
  }
  
  return prevDate;
};

// Constrain date to season bounds
export const constrainDateToSeason = (date, season) => {
  if (date < season.startDate) {
    return season.startDate;
  }
  
  if (date > season.endDate) {
    return season.endDate;
  }
  
  return date;
};
